import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function SendBookingConnectionUpdatedNotificationModal({
  bookingConnectionUpdatedMutationData,
  onClose,
  onSubmit,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.SendBookingConnectionUpdatedNotificationModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const bookingSourceRelation =
    bookingConnectionUpdatedMutationData.data.relationships.source.data;

  const bookingSource = bookingConnectionUpdatedMutationData.included.find(
    (inclusion) =>
      inclusion.type === bookingSourceRelation.type &&
      inclusion.id === bookingSourceRelation.id,
  );

  const course = bookingConnectionUpdatedMutationData.included.find(
    (inclusion) =>
      inclusion.type === 'course' &&
      inclusion.id === bookingSource.relationships.course.data.id,
  );

  const { changedPersonnelCount, unchangedPersonnelIds } =
    bookingConnectionUpdatedMutationData.meta;

  const unchangedPersonnelCount = unchangedPersonnelIds.length;

  const totalNotificationsCount =
    unchangedPersonnelCount + changedPersonnelCount;

  const hasPersonnelChanged = changedPersonnelCount > 0;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {hasPersonnelChanged ?
              i18nT('closeButton.changed', {
                count: changedPersonnelCount,
              })
            : i18nT('closeButton.unchanged')}
          </TextButton>
          <FilledButton color='mint' onClick={onSubmit}>
            {i18nT('submitButton', { count: totalNotificationsCount })}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={onClose}
    >
      <div className='tw-p-6'>
        <h2 className='tw-m-0 tw-mb-6 tw-text-l tw-font-semibold tw-text-grey-900'>
          {hasPersonnelChanged ?
            i18nT('title.changed')
          : i18nT('title.unchanged', { count: totalNotificationsCount })}
        </h2>
        <p className='tw-m-0'>
          {`${i18nTCommon('shared.course')}: `}
          <span className='tw-font-semibold'>{course.attributes.name}</span>
        </p>
        <p className='tw-m-0 tw-mb-6'>
          {`${i18nTCommon('training.trainingDate')}: `}
          <span className='tw-font-semibold'>
            {formatDate({
              date: bookingSource.attributes.date,
              format: 'd MMM yyyy',
            })}
          </span>
        </p>
        <p className='tw-m-0'>
          {i18nT('description', { count: totalNotificationsCount })}
        </p>
      </div>
    </ModalBox>
  );
}

SendBookingConnectionUpdatedNotificationModal.propTypes = {
  bookingConnectionUpdatedMutationData: PropTypes.shape({
    data: resourceShape('bookingConnection'),
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
